import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  useTheme,
} from "@mui/material";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import AppDropDown from "../../../../../views/components/common/filter/AppDropDown";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import FormTextField from "../../../../../views/components/common/form/FormTextField";
import InlineFormError from "../../../../../views/components/common/form/InlineFormError";
import { getModernPageColors } from "../../../../../styles/modernListingPageSx";
import {
  SHIPMENT_CANCEL_REASON_OTHER,
  SHIPMENT_CANCEL_REASONS,
} from "./CancelShipmentPopup";

const TERMINAL_STATUSES = new Set(["COMPLETED", "FAILED"]);

export type BulkCancelScope = "selected" | "filters";

export type BulkCancelJobProgress = {
  status: string;
  totalCount: number;
  processedCount: number;
  cancelledCount: number;
  skippedCount: number;
  failedCount: number;
  errorMessage: string | null;
};

interface BulkCancelShipmentPopupProps {
  open: boolean;
  scope: BulkCancelScope;
  count: number;
  submitting: boolean;
  error: string | null;
  job: BulkCancelJobProgress | null;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}

const CountText: React.FC<{ value: number; color?: string }> = ({
  value,
  color,
}) => (
  <Box component="span" sx={{ color, fontWeight: 700 }}>
    {value}
  </Box>
);

const BulkCancelShipmentPopup: React.FC<BulkCancelShipmentPopupProps> = ({
  open,
  scope,
  count,
  submitting,
  error,
  job,
  onConfirm,
  onClose,
}) => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  const [form, setForm] = useState<{
    selectedReason: FilterValue | null;
    customReason: string;
  }>({ selectedReason: null, customReason: "" });

  useEffect(() => {
    if (!open) {
      setForm({ selectedReason: null, customReason: "" });
    }
  }, [open]);

  const reason =
    form.selectedReason?.value === SHIPMENT_CANCEL_REASON_OTHER
      ? form.customReason.trim()
      : (form.selectedReason?.value ?? "");
  const isValid = reason.trim().length > 0 && count > 0 && !submitting;
  const shipmentWord = count === 1 ? "shipment" : "shipments";
  const countLabel =
    scope === "selected"
      ? `selected ${shipmentWord}`
      : `matching ${shipmentWord}`;
  const confirmLabel = submitting ? "Starting…" : `Cancel ${count}`;

  const jobRunning = Boolean(job && !TERMINAL_STATUSES.has(job.status));
  const busy = jobRunning || submitting;
  const expectedTotal =
    job && job.totalCount > 0
      ? job.totalCount
      : scope === "selected" && count > 0
        ? count
        : job?.totalCount ?? 0;
  const processed = job
    ? Math.max(
        job.processedCount,
        job.cancelledCount + job.failedCount + job.skippedCount,
      )
    : 0;

  const jobStatusLine = !job
    ? null
    : job.status === "COMPLETED"
      ? `Done. Cancelled ${job.cancelledCount}, skipped ${job.skippedCount}, failed ${job.failedCount}.`
      : job.status === "FAILED"
        ? `Stopped. Cancelled ${job.cancelledCount}, skipped ${job.skippedCount}, failed ${job.failedCount}.`
        : expectedTotal > 0
          ? `Cancelling… ${processed} of ${expectedTotal} (cancelled ${job.cancelledCount}, skipped ${job.skippedCount}, failed ${job.failedCount}).`
          : `Cancelling… cancelled ${job.cancelledCount}, skipped ${job.skippedCount}, failed ${job.failedCount}.`;

  const handleConfirm = () => {
    if (!isValid) return;
    onConfirm(reason.trim());
  };

  const handleDialogClose = (
    _event: unknown,
    reasonCode?: "backdropClick" | "escapeKeyDown",
  ) => {
    if (
      busy &&
      (reasonCode === "backdropClick" || reasonCode === "escapeKeyDown")
    ) {
      return;
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      disableEscapeKeyDown={busy}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ pb: 1 }}>
        <CustomTypography variant="heading6" fontWeight={600}>
          Bulk cancel shipments
        </CustomTypography>
      </DialogTitle>
      <DialogContent>
        <Grid2 container spacing={1.5}>
          {job ? (
            <Grid2 size={12}>
              <CustomTypography variant="body2">{jobStatusLine}</CustomTypography>
              {job.errorMessage && (
                <Box sx={{ mt: 1 }}>
                  <InlineFormError message={job.errorMessage} />
                </Box>
              )}
              {error && (
                <Box sx={{ mt: 1 }}>
                  <InlineFormError message={error} />
                </Box>
              )}
            </Grid2>
          ) : (
            <>
              <Grid2 size={12}>
                <CustomTypography variant="body2">
                  You are about to cancel{" "}
                  <CountText
                    value={count}
                    color={theme.palette.customColors?.errorRed}
                  />{" "}
                  {`${countLabel}. This cannot be undone.`}
                </CustomTypography>
                {scope === "filters" && (
                  <CustomTypography
                    variant="body2"
                    sx={{ color: colors.subtitle, mt: 0.75 }}
                  >
                    Needs a customer or order filter. Other filters on this
                    screen also apply.
                  </CustomTypography>
                )}
              </Grid2>
              <Grid2 size={12}>
                <AppDropDown
                  isMultiSelect={false}
                  title="Reason"
                  value={form.selectedReason?.id ?? ""}
                  fontSize="12px"
                  list={SHIPMENT_CANCEL_REASONS.map((item) => ({
                    id: item,
                    value: item,
                  }))}
                  onSingleSelectionChange={(selectedId) =>
                    setForm((current) => ({
                      ...current,
                      selectedReason: {
                        id: selectedId,
                        value: selectedId,
                      },
                    }))
                  }
                />
              </Grid2>
              {form.selectedReason?.value === SHIPMENT_CANCEL_REASON_OTHER && (
                <Grid2 size={12}>
                  <FormTextField
                    id="bulkCancelCustomReason"
                    label="Reason"
                    value={form.customReason}
                    onChange={(_id, value) =>
                      setForm((current) => ({
                        ...current,
                        customReason: value ?? "",
                      }))
                    }
                    multiline
                    rows={2}
                    fontSize="12px"
                  />
                </Grid2>
              )}
              <Grid2 size={12}>
                <InlineFormError message={error ?? undefined} />
              </Grid2>
            </>
          )}
        </Grid2>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 0, gap: 1 }}>
        <AppButton
          variant="secondary"
          label="Close"
          onClick={onClose}
          disabled={submitting}
        />
        {!job && (
          <AppButton
            variant="primary"
            label={confirmLabel}
            onClick={handleConfirm}
            disabled={!isValid}
          />
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkCancelShipmentPopup;
